const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

export const getBankDisplayName = (bank) => bank?.bankShortName || bank?.bankName || bank?.bankCode || '';

export const fetchPaymentBatches = async (tenantId, businessUnitId) => {
  if (!tenantId) return [];

  const batches = businessUnitId
    ? await apiFetch(
      `conductor/payment-batches/by-business-unit?tenant_id=${tenantId}&business_unit_id=${businessUnitId}`,
    )
    : await apiFetch(`conductor/payment-batches/tenant/${tenantId}`);

  return (Array.isArray(batches) ? batches : []).sort((a, b) => {
    const dateA = new Date(a.createdAt || 0);
    const dateB = new Date(b.createdAt || 0);
    return dateB - dateA;
  });
};

export const summarizeBatchBanks = (batch, resolveBankName) => {
  const banks = batch?.banks || [];
  if (!banks.length) return '—';

  const names = banks
    .map((bank) => resolveBankName(bank.bankId))
    .filter(Boolean);

  if (!names.length) return `${banks.length} bank${banks.length === 1 ? '' : 's'}`;
  if (names.length === 1) return names[0];
  if (names.length === 2) return names.join(', ');
  return `${names[0]} (+${names.length - 1})`;
};

export const countBatchPayments = (batch) => (
  (batch?.banks || []).reduce((sum, bank) => sum + (Number(bank.paymentCount) || 0), 0)
);

export const flattenBatchBankSummary = (batch, resolveBankName) => {
  const rows = [];

  (batch?.banks || []).forEach((bank) => {
    const bankName = resolveBankName(bank.bankId);
    const vendors = Array.isArray(bank.vendors) ? bank.vendors : [];

    if (!vendors.length) {
      rows.push({
        vendorName: '',
        bankName,
        paymentMethods: bank.paymentMethods || '',
        paymentCount: bank.paymentCount ?? '',
        totalAmount: bank.totalAmount ?? '',
      });
      return;
    }

    vendors.forEach((vendor) => {
      rows.push({
        vendorName: vendor.vendorName || '',
        bankName,
        paymentMethods: vendor.paymentMethods || bank.paymentMethods || '',
        paymentCount: vendor.paymentCount ?? '',
        totalAmount: vendor.totalAmount ?? '',
      });
    });
  });

  return rows;
};

export const groupBatchBankSummaryByVendor = (rows = []) => {
  const vendorMap = new Map();

  rows.forEach((row) => {
    const vendorKey = row.vendorName || '—';
    if (!vendorMap.has(vendorKey)) {
      vendorMap.set(vendorKey, []);
    }
    vendorMap.get(vendorKey).push(row);
  });

  return Array.from(vendorMap.entries()).map(([vendorName, bankRows]) => ({
    vendorName,
    bankRows,
  }));
};

export const loadPaymentBatchDetail = async (tenantId, batch) => {
  const [transactions, banks, vendors] = await Promise.all([
    apiFetch(
      `conductor/payment-transactions/by-payment-batch?tenant_id=${tenantId}&payment_batch_id=${batch.paymentBatchId}`,
    ).catch(() => []),
    apiFetch(`conductor/cash-recon/banks/by-tenant-and-status?tenant_id=${tenantId}&status=ACTIVE`).catch(() => []),
    apiFetch(`conductor/vendor-masters/tenant/${tenantId}`).catch(() => []),
  ]);

  const bankMap = Object.fromEntries(
    (Array.isArray(banks) ? banks : []).map((bank) => [bank.bankId, bank]),
  );
  const vendorMap = Object.fromEntries(
    (Array.isArray(vendors) ? vendors : []).map((vendor) => [vendor.vendorId, vendor]),
  );
  const resolveBankName = (bankId) => getBankDisplayName(bankMap[bankId]);
  const resolveVendorName = (vendorId) => vendorMap[vendorId]?.vendorName || '—';

  const paymentLines = await Promise.all(
    (Array.isArray(transactions) ? transactions : []).map(async (transaction) => {
      let invoiceNumber = '—';
      try {
        const mappings = await apiFetch(
          `conductor/invoice-payment-mappings/by-payment?tenant_id=${tenantId}&payment_id=${transaction.paymentId}`,
        );
        const mapping = Array.isArray(mappings) ? mappings[0] : null;
        if (mapping?.apInvoiceId) {
          const invoice = await apiFetch(`conductor/ap-invoice-headers/${mapping.apInvoiceId}`);
          invoiceNumber = invoice?.invoiceNumber || invoiceNumber;
        }
      } catch {
        // keep fallback invoice number from reference
      }

      if (invoiceNumber === '—' && transaction.paymentReference) {
        const parts = String(transaction.paymentReference).split('-');
        if (parts.length >= 3) {
          invoiceNumber = parts.slice(2, -1).join('-') || parts[parts.length - 2] || '—';
        }
      }

      return {
        paymentId: transaction.paymentId,
        invoiceNumber,
        vendorName: resolveVendorName(transaction.vendorId),
        paymentAmount: Number(transaction.paymentAmount) || 0,
        paymentMethod: transaction.paymentMethod || '—',
        bankName: resolveBankName(transaction.bankId),
        paymentReference: transaction.paymentReference || '—',
        paymentStatus: transaction.paymentStatus || '—',
      };
    }),
  );

  return {
    bankSummary: flattenBatchBankSummary(batch, resolveBankName),
    vendorBankGroups: groupBatchBankSummaryByVendor(flattenBatchBankSummary(batch, resolveBankName)),
    paymentLines,
    resolveBankName,
  };
};
